// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/state-machines/initial-sync-machine.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/state-machines/src/initial-sync-machine.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo apunte-digital-app
//     node fleet/fleet.ts check --repo apunte-digital-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import { assign, setup } from "xstate";

// --- Types ---

export interface InitialSyncContext {
  /** Whether the user has been identified (logged in) */
  hasUser: boolean;
  /** Whether the device is currently online */
  isOnline: boolean;
  /** Whether the app is in the auth flow (login/register screens) */
  inAuthGroup: boolean;
  /** Whether PowerSync has completed at least one full sync */
  hasSynced: boolean;
  /** Whether PowerSync is actively downloading */
  isDownloading: boolean;
  /** Whether PowerSync is connected or connecting */
  isConnectedOrConnecting: boolean;
  /** Internal tracking for when the initial sync attempts started to avoid infinite checking */
  syncStartTime: number | null;
}

export type InitialSyncEvent =
  | {
      type: "STATUS_CHANGED";
      hasSynced: boolean;
      isDownloading: boolean;
      isConnectedOrConnecting: boolean;
      isOnline: boolean;
    }
  | { type: "USER_CHANGED"; hasUser: boolean; inAuthGroup: boolean }
  | { type: "MINIMIZE" }
  | { type: "MAXIMIZE" };

// --- UI Meta ---

export interface InitialSyncUIMeta {
  /** Whether to show the full-screen blocking overlay */
  showFullScreen: boolean;
  /** Whether to show the minimized floating toast */
  showMinimizedToast: boolean;
  /** Whether to show a spinner */
  showSpinner: boolean;
  /** Whether to show the progress bar */
  showProgress: boolean;
  /** Whether to show action buttons (Continue Offline, Sign out) */
  showActions: boolean;
  /** Whether the state represents an offline condition */
  isOffline: boolean;
  /** I18n label key for the main title */
  titleKey: "settingUp" | "offline" | "none";
  /** I18n label key for the description */
  descriptionKey: "connecting" | "downloading" | "offline" | "stuck" | "syncing" | "none";
}

const initialSyncUI: Record<InitialSyncStateName, InitialSyncUIMeta> = {
  passthrough: {
    showFullScreen: false,
    showMinimizedToast: false,
    showSpinner: false,
    showProgress: false,
    showActions: false,
    isOffline: false,
    titleKey: "none",
    descriptionKey: "none",
  },
  connecting: {
    showFullScreen: true,
    showMinimizedToast: false,
    showSpinner: true,
    showProgress: false,
    showActions: true,
    isOffline: false,
    titleKey: "settingUp",
    descriptionKey: "connecting",
  },
  downloading: {
    showFullScreen: true,
    showMinimizedToast: false,
    showSpinner: true,
    showProgress: true,
    showActions: true,
    isOffline: false,
    titleKey: "settingUp",
    descriptionKey: "downloading",
  },
  offline: {
    showFullScreen: true,
    showMinimizedToast: false,
    showSpinner: false,
    showProgress: false,
    showActions: true,
    isOffline: true,
    titleKey: "offline",
    descriptionKey: "offline",
  },
  stuck: {
    showFullScreen: true,
    showMinimizedToast: false,
    showSpinner: true,
    showProgress: false,
    showActions: true,
    isOffline: false,
    titleKey: "settingUp",
    descriptionKey: "stuck",
  },
  minimized: {
    showFullScreen: false,
    showMinimizedToast: true,
    showSpinner: false,
    showProgress: false,
    showActions: false,
    isOffline: false,
    titleKey: "none",
    descriptionKey: "syncing",
  },
  synced: {
    showFullScreen: false,
    showMinimizedToast: false,
    showSpinner: false,
    showProgress: false,
    showActions: false,
    isOffline: false,
    titleKey: "none",
    descriptionKey: "none",
  },
};

// --- Machine ---

const SYNC_TIMEOUT_MS = 10_000;

export const initialSyncMachine = setup({
  // Schema Definition Pattern for XState v5 type inference
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  types: {} as {
    context: InitialSyncContext;
    events: InitialSyncEvent;
  },
  actions: {
    updateStatus: assign({
      hasSynced: ({ event }) => (event.type === "STATUS_CHANGED" ? event.hasSynced : false),
      isDownloading: ({ event }) => (event.type === "STATUS_CHANGED" ? event.isDownloading : false),
      isConnectedOrConnecting: ({ event }) =>
        event.type === "STATUS_CHANGED" ? event.isConnectedOrConnecting : false,
      isOnline: ({ event }) => (event.type === "STATUS_CHANGED" ? event.isOnline : false),
    }),
    updateUser: assign({
      hasUser: ({ event }) => (event.type === "USER_CHANGED" ? event.hasUser : false),
      inAuthGroup: ({ event }) => (event.type === "USER_CHANGED" ? event.inAuthGroup : false),
      syncStartTime: ({ context, event }) => {
        const hasUser = event.type === "USER_CHANGED" ? event.hasUser : false;
        const inAuthGroup = event.type === "USER_CHANGED" ? event.inAuthGroup : false;

        // If we are just starting the sync flow (hasUser && !inAuthGroup), set the start time
        if (hasUser && !inAuthGroup && context.syncStartTime === null) {
          return Date.now();
        }
        // If user logs out, reset it
        if (!hasUser) {
          return null;
        }
        return context.syncStartTime;
      },
    }),
  },
  guards: {
    shouldPassthrough: ({ context, event }) => {
      const hasUser = event.type === "USER_CHANGED" ? event.hasUser : context.hasUser;
      const inAuthGroup = event.type === "USER_CHANGED" ? event.inAuthGroup : context.inAuthGroup;
      return !hasUser || inAuthGroup;
    },
    hasSynced: ({ context, event }) =>
      event.type === "STATUS_CHANGED" ? event.hasSynced : context.hasSynced,
    isOffline: ({ context, event }) => {
      const isOnline = event.type === "STATUS_CHANGED" ? event.isOnline : context.isOnline;
      const isConnected =
        event.type === "STATUS_CHANGED"
          ? event.isConnectedOrConnecting
          : context.isConnectedOrConnecting;
      return !isOnline && !isConnected;
    },
    isDownloading: ({ context, event }) =>
      event.type === "STATUS_CHANGED" ? event.isDownloading : context.isDownloading,
    wentOnline: ({ context, event }) => {
      const isOnline = event.type === "STATUS_CHANGED" ? event.isOnline : context.isOnline;
      const isConnected =
        event.type === "STATUS_CHANGED"
          ? event.isConnectedOrConnecting
          : context.isConnectedOrConnecting;
      return isOnline || isConnected;
    },
    isTimeoutReached: ({ context }) => {
      if (!context.syncStartTime) {
        return false;
      }
      return Date.now() - context.syncStartTime > SYNC_TIMEOUT_MS;
    },
  },
  delays: {
    SYNC_TIMEOUT: SYNC_TIMEOUT_MS,
  },
}).createMachine({
  id: "initialSync",
  initial: "passthrough",
  context: {
    hasUser: false,
    isOnline: true,
    inAuthGroup: true,
    hasSynced: false,
    isDownloading: false,
    isConnectedOrConnecting: false,
    syncStartTime: null,
  },
  states: {
    // User not logged in or in auth screens → just render children
    passthrough: {
      on: {
        USER_CHANGED: [
          { target: "passthrough", guard: "shouldPassthrough", actions: "updateUser" },
          { target: "connecting", actions: "updateUser" },
        ],
        STATUS_CHANGED: [
          { target: "synced", guard: "hasSynced", actions: "updateStatus" },
          { actions: "updateStatus" },
        ],
      },
      always: [
        {
          target: "connecting",
          guard: ({ context }) => context.hasUser && !context.inAuthGroup && !context.hasSynced,
        },
      ],
    },

    // User is logged in, trying to establish first connection
    connecting: {
      after: {
        SYNC_TIMEOUT: "stuck",
      },
      on: {
        STATUS_CHANGED: [
          { target: "synced", guard: "hasSynced", actions: "updateStatus" },
          { target: "downloading", guard: "isDownloading", actions: "updateStatus" },
          { target: "offline", guard: "isOffline", actions: "updateStatus" },
          { actions: "updateStatus" },
        ],
        USER_CHANGED: [
          { target: "passthrough", guard: "shouldPassthrough", actions: "updateUser" },
          { actions: "updateUser" },
        ],
        MINIMIZE: "minimized",
      },
      always: [
        { target: "stuck", guard: "isTimeoutReached" },
        { target: "offline", guard: "isOffline" },
      ],
    },

    // Actively downloading data from PowerSync
    downloading: {
      after: {
        SYNC_TIMEOUT: "stuck",
      },
      on: {
        STATUS_CHANGED: [
          { target: "synced", guard: "hasSynced", actions: "updateStatus" },
          { target: "offline", guard: "isOffline", actions: "updateStatus" },
          { actions: "updateStatus" },
        ],
        USER_CHANGED: [
          { target: "passthrough", guard: "shouldPassthrough", actions: "updateUser" },
          { actions: "updateUser" },
        ],
        MINIMIZE: "minimized",
      },
      always: [{ target: "stuck", guard: "isTimeoutReached" }],
    },

    // No internet connection
    offline: {
      on: {
        STATUS_CHANGED: [
          { target: "synced", guard: "hasSynced", actions: "updateStatus" },
          { target: "connecting", guard: "wentOnline", actions: "updateStatus" },
          { actions: "updateStatus" },
        ],
        USER_CHANGED: [
          { target: "passthrough", guard: "shouldPassthrough", actions: "updateUser" },
          { actions: "updateUser" },
        ],
        MINIMIZE: "minimized",
      },
      always: [{ target: "stuck", guard: "isTimeoutReached" }],
    },

    // Sync is taking too long (timeout reached)
    stuck: {
      entry: () => {
        console.log("[InitialSyncScreen] Sync timeout reached, entering stuck state");
      },
      after: {
        // Auto-minimize after appearing stuck for a moment
        SYNC_TIMEOUT: "minimized",
      },
      on: {
        STATUS_CHANGED: [
          { target: "synced", guard: "hasSynced", actions: "updateStatus" },
          { actions: "updateStatus" },
        ],
        USER_CHANGED: [
          { target: "passthrough", guard: "shouldPassthrough", actions: "updateUser" },
          { actions: "updateUser" },
        ],
        MINIMIZE: "minimized",
      },
    },

    // Full-screen dismissed, show floating toast while syncing
    minimized: {
      on: {
        STATUS_CHANGED: [
          { target: "synced", guard: "hasSynced", actions: "updateStatus" },
          { actions: "updateStatus" },
        ],
        USER_CHANGED: [
          { target: "passthrough", guard: "shouldPassthrough", actions: "updateUser" },
          { actions: "updateUser" },
        ],
        MAXIMIZE: [
          { target: "downloading", guard: "isDownloading" },
          { target: "offline", guard: "isOffline" },
          { target: "connecting" },
        ],
      },
    },

    // First sync completed! Render children permanently.
    synced: {
      type: "final",
    },
  },
});

// --- Types (continued) ---

export type InitialSyncStateName =
  | "passthrough"
  | "connecting"
  | "downloading"
  | "offline"
  | "stuck"
  | "minimized"
  | "synced";

// --- Helpers ---

export function getInitialSyncUI(stateName: InitialSyncStateName): InitialSyncUIMeta {
  // We widen the type during lookup to allow for a runtime fallback
  // without triggering 'no-unnecessary-condition' linting warnings.
  const ui = (initialSyncUI as Record<string, InitialSyncUIMeta>)[stateName];
  return ui ?? initialSyncUI.passthrough;
}
